import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as Popover from "@radix-ui/react-popover";
import { BookMarked, BookPlus, CheckCircle2, MoreHorizontal } from "lucide-react";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  create_vocabulary_entry,
  get_vocabulary_catalogue,
  get_vocabulary_entries,
  update_vocabulary_entry,
} from "../../api";
import type { VocabularyEntryDb, VocabularyEntryForm, VocabularyScope } from "../../api/types";
import { useLogin } from "../../reducers";
import { isAdmin } from "../../utils/permissions";
import { normalizeName } from "../../utils/vocabulary";
import { HelpHint } from "./processSidebarUi";

/** One editable name in the form: `value` is what gets saved, `key` the name it
 * started as (its identity for React and for the checkbox label). */
type NameRow = { key: string; value: string; checked: boolean };

const toRows = (names: readonly string[], checked: boolean): NameRow[] =>
  names.map(name => ({ key: name, value: name, checked }));

const dedupe = (names: readonly string[]) => [...new Set(names)];

/** A checkable list of names, each editable in place: unchecking drops the name
 * from the entry, retyping it replaces that name with the new spelling. Editing
 * checks the row, since a name worth rewording is a name worth keeping. */
const NameList = ({
  label,
  rows,
  setRows,
  busy,
}: {
  label: string;
  rows: NameRow[];
  setRows: React.Dispatch<React.SetStateAction<NameRow[]>>;
  busy: boolean;
}) => {
  const { t } = useTranslation();
  const allChecked = rows.every(row => row.checked);
  const patch = (key: string, change: Partial<NameRow>) =>
    setRows(prev => prev.map(row => (row.key === key ? { ...row, ...change } : row)));

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wide text-text-dim">
          {label}
        </span>
        <button
          type="button"
          onClick={() => setRows(prev => prev.map(row => ({ ...row, checked: !allChecked })))}
          disabled={busy}
          className="text-xs text-text-dim underline-offset-2 transition-colors hover:text-text hover:underline"
        >
          {allChecked ? t("vocabulary_select_none") : t("vocabulary_select_all")}
        </button>
      </div>
      <div className="max-h-[9rem] space-y-1 overflow-y-auto rounded-md border border-outline bg-background px-2 py-1.5">
        {rows.map(row => (
          <div key={row.key} className="flex items-center gap-2">
            <input
              type="checkbox"
              aria-label={row.key}
              checked={row.checked}
              onChange={() => patch(row.key, { checked: !row.checked })}
              disabled={busy}
            />
            <input
              type="text"
              value={row.value}
              title={row.value}
              onChange={e => patch(row.key, { value: e.target.value, checked: true })}
              disabled={busy}
              aria-invalid={row.checked && row.value.trim() === ""}
              className={`min-w-0 flex-1 rounded border bg-transparent px-1 py-0.5 text-sm outline-none transition hover:border-outline focus:border-primary focus:text-text ${
                row.checked && row.value.trim() === "" ? "border-danger" : "border-transparent"
              } ${row.checked ? "text-text-mid" : "text-text-dim/70"}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

/** The form itself, remounted (via `key`) whenever the definition it starts
 * from changes, so the state initialisers below stay the only place the
 * defaults are decided. */
const VocabularyForm = ({
  processName,
  savedSteps,
  observedSteps,
  hasDefinition,
  knownKeys,
  initialScope,
  onSave,
}: {
  processName: string;
  savedSteps: readonly string[];
  observedSteps: readonly string[];
  hasDefinition: boolean;
  knownKeys: ReadonlySet<string>;
  initialScope: VocabularyScope;
  onSave: (form: VocabularyEntryForm) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const { permissions } = useLogin();
  const processFieldId = useId();
  const processFieldRef = useRef<HTMLInputElement>(null);
  const [processInput, setProcessInput] = useState(processName);
  const [savedRows, setSavedRows] = useState(() => toRows(savedSteps, true));
  // Names this analysis produced that no definition carries yet: offered, but
  // off by default, so saving only ever keeps names that were picked on purpose.
  const [newRows, setNewRows] = useState(() => toRows(observedSteps, false));
  const [scope, setScope] = useState<VocabularyScope>(initialScope);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The popover hands focus to this field rather than to its first tabbable -
  // the question mark beside the title, which would greet everyone with its
  // tooltip. Paired with `onOpenAutoFocus` being prevented on the content.
  useEffect(() => {
    processFieldRef.current?.focus();
  }, []);

  // True whenever saving lands on an existing definition rather than creating
  // one - including after a rename, which renames that same definition.
  const replacesExisting = hasDefinition || knownKeys.has(normalizeName(processInput));

  // A name that was blanked out is a mistake, not a way to drop it: unchecking
  // is. And an entry with no steps at all says nothing, so both hold saving
  // back. The complaint is derived rather than stored, so it clears itself the
  // moment the form is fixed - but only appears once saving was actually tried.
  const rows = [...savedRows, ...newRows];
  const complaint = rows.some(row => row.checked && row.value.trim() === "")
    ? t("vocabulary_blank_name")
    : rows.some(row => row.checked)
      ? null
      : t("vocabulary_no_names");

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (complaint !== null) return;
    setBusy(true);
    setError(null);
    const seen = new Set<string>();
    const steps: string[] = [];
    for (const row of rows) {
      const value = row.value.trim();
      if (!row.checked || !value) continue;
      const key = normalizeName(value);
      if (seen.has(key)) continue;
      seen.add(key);
      steps.push(value);
    }
    try {
      await onSave({ process: processInput, steps, scope });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="min-h-0 flex-1 space-y-3.5 overflow-y-auto px-4 py-3">
      <div className="space-y-1.5">
        <label
          htmlFor={processFieldId}
          className="block text-[11px] font-medium uppercase tracking-wide text-text-dim"
        >
          {t("vocabulary_popover_process_label")}
        </label>
        <input
          id={processFieldId}
          ref={processFieldRef}
          className="w-full rounded-md border border-outline bg-background px-2 py-1 text-sm text-text outline-none transition focus:border-primary"
          value={processInput}
          onChange={e => setProcessInput(e.target.value)}
          disabled={busy}
          required
        />
        {replacesExisting && (
          <p className="text-xs leading-relaxed text-text-dim">{t("vocabulary_override_hint")}</p>
        )}
      </div>
      {savedRows.length > 0 && (
        <NameList
          label={t("vocabulary_saved_steps_label")}
          rows={savedRows}
          setRows={setSavedRows}
          busy={busy}
        />
      )}
      {newRows.length > 0 && (
        <NameList
          label={
            savedRows.length > 0
              ? t("vocabulary_new_steps_label")
              : t("vocabulary_popover_steps_label")
          }
          rows={newRows}
          setRows={setNewRows}
          busy={busy}
        />
      )}
      {isAdmin(permissions) && (
        <label className="flex items-center gap-2 text-sm text-text-mid">
          <span className="text-[11px] font-medium uppercase tracking-wide text-text-dim">
            {t("vocabulary_scope_label")}
          </span>
          <select
            value={scope}
            onChange={e => setScope(e.target.value as VocabularyScope)}
            disabled={busy}
            className="flex-1 rounded-md border border-outline bg-background px-2 py-1 text-sm text-text"
          >
            <option value="user">{t("vocabulary_scope_user")}</option>
            <option value="company">{t("vocabulary_scope_company")}</option>
          </select>
        </label>
      )}
      {(submitted && complaint) || error ? (
        <p role="alert" className="text-xs text-danger">
          {(submitted && complaint) || error}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={busy || !processInput.trim()}
        className="w-full rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy ? t("vocabulary_updating_entry") : t("vocabulary_save_names")}
      </button>
    </form>
  );
};

/**
 * The "standardize the names of this observed process" popover: prefilled with
 * the process name and its observed steps, so contributing means confirming
 * names the analysis already produced rather than typing new ones. When a
 * definition for this process already exists - the caller's own, or one the
 * catalogue reaches them with - the popover starts from its names, and saving
 * replaces it.
 */
const AddToVocabulary = ({
  processName,
  stepNames,
  inActionsMenu = false,
}: {
  processName: string;
  stepNames: string[];
  inActionsMenu?: boolean;
}) => {
  const { t } = useTranslation();
  const { user } = useLogin();
  const observed = useMemo(() => dedupe(stepNames), [stepNames]);
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const [saved, setSaved] = useState(false);
  const [knownKeys, setKnownKeys] = useState<ReadonlySet<string>>(() => new Set());
  // The names each already-known process is defined with, so an existing
  // definition can be edited rather than blindly overwritten.
  const [knownSteps, setKnownSteps] = useState<ReadonlyMap<string, string[]>>(() => new Map());
  const [myEntry, setMyEntry] = useState<VocabularyEntryDb | null>(null);

  // The trigger label and the form's starting point both depend on what already
  // exists, so catalogue and entries are fetched on mount. It's a nicety: on
  // failure the popover just offers plain creation.
  useEffect(() => {
    let cancelled = false;
    Promise.all([get_vocabulary_catalogue(), get_vocabulary_entries()])
      .then(([catalogue, entries]) => {
        if (cancelled) return;
        const keys = new Set<string>();
        const catalogueSteps = new Map<string, string[]>();
        // `contributed` is the merged view of the entries reaching the caller,
        // so it wins over the base catalogue where both name the same process.
        for (const macro of [...catalogue.base, ...catalogue.contributed]) {
          const key = normalizeName(macro.process);
          keys.add(key);
          catalogueSteps.set(key, dedupe(macro.steps ?? []));
        }
        const target = normalizeName(processName);
        let mine: VocabularyEntryDb | null = null;
        for (const entry of entries) {
          if (entry.payload.v !== "1") continue;
          keys.add(entry.normalised_process_name);
          if (
            entry.user_id === user.id &&
            entry.normalised_process_name === target &&
            (mine === null || entry._updated_at > mine._updated_at)
          ) {
            mine = entry;
          }
        }
        setKnownKeys(keys);
        setKnownSteps(catalogueSteps);
        setMyEntry(mine);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [processName, user.id]);

  const target = normalizeName(processName);
  const mine = myEntry !== null && myEntry.normalised_process_name === target ? myEntry : null;
  const minePayload = mine !== null && mine.payload.v === "1" ? mine.payload : null;
  const savedSteps =
    minePayload !== null ? dedupe(minePayload.steps ?? []) : (knownSteps.get(target) ?? []);
  const hasDefinition = minePayload !== null || knownKeys.has(target);
  // The second list only offers what the definition doesn't already carry.
  const savedKeys = new Set(savedSteps.map(normalizeName));
  const newSteps = observed.filter(name => !savedKeys.has(normalizeName(name)));

  // Saving closes the popover: the confirmation is a passing toast, since
  // there's nothing left to do in the form once the names are in.
  const save = async (form: VocabularyEntryForm) => {
    if (mine !== null) {
      setMyEntry(await update_vocabulary_entry(mine.id, form));
    } else {
      const created = await create_vocabulary_entry(form);
      setMyEntry(created);
      setKnownKeys(prev => new Set([...prev, created.normalised_process_name]));
    }
    setOpen(false);
    setSaved(true);
  };

  useEffect(() => {
    if (!saved) return;
    const timer = window.setTimeout(() => setSaved(false), 4000);
    return () => window.clearTimeout(timer);
  }, [saved]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <span className="relative inline-flex shrink-0">
        {inActionsMenu ? (
          <DropdownMenu.Root open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
            <Popover.Anchor asChild>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  ref={moreButtonRef}
                  aria-label={t("process_more_actions")}
                  title={t("process_more_actions")}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-text-dim hover:bg-muted-surface focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <MoreHorizontal size={18} aria-hidden="true" />
                </button>
              </DropdownMenu.Trigger>
            </Popover.Anchor>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={6}
                onCloseAutoFocus={event => {
                  if (open) event.preventDefault();
                }}
                className="pd-theme z-50 rounded-lg border border-outline bg-surface p-1 shadow-lift"
              >
                <DropdownMenu.Item
                  onSelect={() => {
                    setMenuOpen(false);
                    setOpen(true);
                  }}
                  className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm text-text outline-none focus:bg-muted-surface"
                >
                  <BookMarked size={15} aria-hidden="true" />
                  {t("change_vocabulary")}
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        ) : (
          <Popover.Trigger asChild>
            <button
              type="button"
              className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-outline bg-surface px-3 text-xs font-medium text-text-mid transition hover:bg-surface-strong hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              {/* The icon follows the label: a book being added to, or one already bookmarked once a
                definition for this process exists. */}
              {hasDefinition ? (
                <BookMarked className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <BookPlus className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {hasDefinition ? t("change_vocabulary") : t("add_to_vocabulary")}
            </button>
          </Popover.Trigger>
        )}
        {/* The confirmation sits right under the button that was just pressed,
            where the eye already is, rather than at the foot of the page. */}
        {saved && (
          <div
            role="status"
            className="pointer-events-none absolute right-0 top-full z-[100] mt-2 flex items-center gap-2 whitespace-nowrap rounded-lg border border-outline bg-surface-strong/95 px-3 py-2 text-sm text-text shadow-xl backdrop-blur-md"
          >
            <CheckCircle2 size={16} className="shrink-0 text-success-strong" aria-hidden="true" />
            <span>{t("vocabulary_names_saved")}</span>
          </div>
        )}
      </span>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          collisionPadding={12}
          aria-label={t("vocabulary_popover_title")}
          onOpenAutoFocus={event => {
            if (!inActionsMenu) event.preventDefault();
          }}
          onCloseAutoFocus={event => {
            if (inActionsMenu) {
              event.preventDefault();
              moreButtonRef.current?.focus();
            }
          }}
          className="pd-theme z-50 flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-outline bg-surface shadow-lift"
          style={{
            maxHeight:
              "min(var(--radix-popover-content-available-height, 100vh), calc(100vh - 6rem))",
          }}
        >
          <div className="flex shrink-0 items-center gap-1.5 border-b border-outline bg-surface-strong/50 px-4 py-3">
            <p className="text-sm font-semibold text-text">{t("vocabulary_popover_title")}</p>
            <HelpHint
              standalone
              side="bottom"
              align="end"
              label={t("vocabulary_popover_help")}
              text={t("vocabulary_popover_lede")}
            />
          </div>
          <VocabularyForm
            key={[processName, ...savedSteps].join("\n")}
            processName={minePayload?.process ?? processName}
            savedSteps={savedSteps}
            observedSteps={newSteps}
            hasDefinition={hasDefinition}
            knownKeys={knownKeys}
            initialScope={minePayload?.scope ?? "user"}
            onSave={save}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default AddToVocabulary;
